import { useEffect, useMemo, useState } from 'react';
import { Search, Music, TrendingUp } from 'lucide-react';
import api, { type ProductSchema } from '../api';
import { useStore } from '../store/useStore';
import DanceCard from '../components/DanceCard';
import Modal from '../components/Modal';
import { toast } from '../components/Toast';
import type { DanceType, Dance } from '../types';
import { normalizeTaskStatus, type TaskItem } from '../utils/taskUtils';

const PAGE_LIMIT = 20;

const fmtDur = (s: number) => {
  const total = Math.round(s);
  const m = String(Math.floor(total / 60)).padStart(2, '0');
  const sec = String(total % 60).padStart(2, '0');
  return `${m}:${sec}`;
};

const mapTaskToDance = (task: TaskItem): Dance => {
  const id = String(task.id ?? task.task_id ?? '');
  const name = task.name ?? task.task_name ?? '未命名舞蹈';
  const normalized = normalizeTaskStatus(task.status ?? task.task_status);
  // DanceStatus 不含 pending，等待中任务在卡片上按 generating 展示
  const status = normalized === 'pending' ? 'generating' : normalized;

  return {
    id,
    name,
    type: 'simulation',
    status,
    stage: task.stage,
    duration: task.duration != null ? fmtDur(Number(task.duration)) : '00:00',
    robotModel: task.robot_model ?? task.robotModel ?? '未知设备',
    imageUrl: task.cover_url ?? task.image_url ?? task.imageUrl ?? '',
    videoUrl: task.video_url,
    createdAt: task.created_at ?? task.created_timestamp ?? task.createdAt ?? '',
    progress: task.progress,
    simulationId: task.simulation_id ?? undefined,
    mediaId: task.media_id ?? undefined,
  };
};

export default function MyDances() {
  const { dances, updateDance, deleteDance } = useStore();
  const [activeTab, setActiveTab] = useState<DanceType>('simulation');
  const [searchQuery, setSearchQuery] = useState('');
  const [simulationDances, setSimulationDances] = useState<Dance[]>([]);
  const [loadingSimulation, setLoadingSimulation] = useState(false);

  // 训练模型弹窗状态
  const [showTrainModal, setShowTrainModal] = useState(false);
  const [trainingDance, setTrainingDance] = useState<Dance | null>(null);
  const [trainProduct, setTrainProduct] = useState<ProductSchema | null>(null);
  const [trainQuote, setTrainQuote] = useState<number | null>(null);
  const [trainBalance, setTrainBalance] = useState<number | null>(null);
  const [trainLoading, setTrainLoading] = useState(false);

  useEffect(() => {
    const loadSimulationDances = async () => {
      if (activeTab !== 'simulation') return;
      setLoadingSimulation(true);
      try {
        const response = await api.task.list({
          limit: PAGE_LIMIT,
          offset: 0,
          origin_type: 'user',
        });
        const list = response.items ?? [];
        setSimulationDances(list.map(mapTaskToDance).filter((dance) => dance.id));
      } catch (error) {
        console.error('Load simulation dances failed:', error);
        toast('error', '仿真视频列表加载失败');
      } finally {
        setLoadingSimulation(false);
      }
    };

    loadSimulationDances();
  }, [activeTab]);

  const filteredDances = useMemo(() => {
    const source = activeTab === 'simulation' ? simulationDances : dances.filter((d) => d.type === 'model');
    return source.filter((d) => d.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [activeTab, dances, searchQuery, simulationDances]);

  const currentCount = activeTab === 'simulation' ? simulationDances.length : dances.filter((d) => d.type === 'model').length;

  const handleDelete = async (id: string) => {
    if (activeTab === 'simulation') {
      try {
        await api.delete(`/api/v1/task/${id}`);
        setSimulationDances((prev) => prev.filter((dance) => dance.id !== id));
        toast('success', '舞蹈已删除');
      } catch (error) {
        console.error('Delete task failed:', error);
        toast('error', '删除失败，请稍后重试');
      }
      return;
    }

    deleteDance(id);
    toast('success', '舞蹈已删除');
  };

  const handleRename = async (id: string, name: string) => {
    const nextName = name.trim();
    if (!nextName) {
      toast('warning', '舞蹈名称不能为空');
      return;
    }

    if (activeTab === 'simulation') {
      try {
        await api.patch(`/api/v1/task/${id}`, { task_name: nextName });
        setSimulationDances((prev) => prev.map((dance) => (dance.id === id ? { ...dance, name: nextName } : dance)));
        toast('success', '重命名成功');
      } catch (error) {
        console.error('Rename task failed:', error);
        toast('error', '重命名失败');
      }
      return;
    }

    updateDance(id, { name: nextName });
    toast('success', '重命名成功');
  };
  
  const handleTrain = (dance: Dance) => {
    setTrainingDance(dance);
    setShowTrainModal(true);
    setTrainProduct(null);
    setTrainQuote(null);
    setTrainBalance(null);

    void (async () => {
      setTrainLoading(true);
      try {
        const [products, account] = await Promise.all([
          api.product.list({ page_size: 20, page_index: 1, training_entrance: 'model' }),
          api.billing.getAccount(),
        ]);
        setTrainBalance(account.availableAmount);

        const product = products.products?.find((p) => p.is_recommended) ?? products.products?.[0] ?? null;
        setTrainProduct(product);

        if (product && dance.mediaId) {
          const quoteRes = await api.product.quote({ media_id: dance.mediaId, product_id: product.product_id, platform: 'edit' });
          setTrainQuote(quoteRes.quote);
        } else {
          setTrainQuote(null);
        }

        // 时长直接使用任务列表里的 duration；这里仅从任务详情补齐 simulation_id（列表里可能为空）
        try {
          const detail = await api.task.detail(dance.id, { origin_type: 'user' });
          const simulationId = detail.simulation_id;
          setTrainingDance((prev) =>
            prev ? { ...prev, simulationId: simulationId ?? prev.simulationId } : prev,
          );
        } catch (err) {
          console.error('加载任务详情获取 simulation_id 失败:', err);
        }
      } catch (error) {
        console.error('Load train product failed:', error);
        toast('error', '加载训练信息失败');
      } finally {
        setTrainLoading(false);
      }
    })();
  };

  const handleConfirmTrain = async () => {
    const simulationId = trainingDance?.simulationId;
    if (!trainingDance || !simulationId) {
      toast('warning', '缺少仿真 ID，无法训练');
      return;
    }

    setTrainLoading(true);
    try {
      await api.model.create({ simulation_id: simulationId, model_name: trainingDance.name });
      toast('success', '训练任务已创建');
      setShowTrainModal(false);
      setActiveTab('model');
    } catch (error) {
      console.error('Create train model failed:', error);
      toast('error', '创建训练任务失败，请稍后重试');
    } finally {
      setTrainLoading(false);
    }
  };

  const handleDeploy = () => { toast('info', '舞蹈模型已同步至手机端，请在「Oakay」App → ODance 中连接机器人并执行'); };
  const handleRetry = (id: string) => { updateDance(id, { status: 'generating', progress: 0 }); toast('success', '已重新提交生成任务'); };

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-end justify-between mb-1">
          <div>
            <h1 className="text-2xl font-bold text-text-primary font-display">我的舞蹈</h1>
            <p className="text-sm text-text-secondary mt-1">管理您的舞蹈作品和生成任务</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-text-muted">
            <TrendingUp size={14} />
            <span>共 {currentCount} 个作品</span>
          </div>
        </div>
      </div>

      {/* Tabs & Search */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex bg-white rounded-2xl p-1 shadow-soft border border-border-subtle">
          {(['simulation', 'model'] as DanceType[]).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${activeTab === tab ? 'gradient-brand text-white shadow-md shadow-brand/20' : 'text-text-secondary hover:text-text-primary hover:bg-slate-50'}`}>
              {tab === 'simulation' ? '仿真视频' : '舞蹈模型'}
              <span className={`ml-1.5 text-[11px] tabular-nums ${activeTab === tab ? 'text-white/60' : 'text-text-muted'}`}>
                ({tab === 'simulation' ? simulationDances.length : dances.filter((d) => d.type === 'model').length})
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="搜索舞蹈..."
              className="pl-10 pr-4 py-2.5 w-56 border border-border-main rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-all shadow-soft" />
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      {filteredDances.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filteredDances.map((dance, idx) => (
            <div key={dance.id} className="card-stagger" style={{ animationDelay: `${idx * 60}ms` }}>
              <DanceCard dance={dance} onRename={handleRename}
                onDelete={handleDelete} onRetry={handleRetry} onTrain={handleTrain} onDeploy={handleDeploy} />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mb-5">
            <Music size={32} className="text-text-muted" />
          </div>
          <p className="text-sm text-text-secondary font-semibold">{searchQuery ? '没有找到匹配的舞蹈' : '暂无舞蹈作品'}</p>
          <p className="text-xs text-text-muted mt-1.5">{searchQuery ? '尝试其他关键词' : '点击右上角「创建舞蹈」开始'}</p>
        </div>
      )}

      {/* 训练模型确认 */}
      <Modal isOpen={showTrainModal} onClose={() => setShowTrainModal(false)} title="训练模型" width="max-w-sm">
        <p className="text-xs text-text-muted mb-2 text-center">请再次确认以下信息</p>
        <p className="text-xs text-warning-dark text-center mb-4">确认后将扣除对应的橡树果</p>
        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-muted">舞蹈名称</span>
            <span className="font-semibold text-text-primary">{trainingDance?.name}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-muted">机器人型号</span>
            <span className="font-semibold text-text-primary">{trainingDance?.robotModel}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-muted">舞蹈时长</span>
            <span className="font-semibold text-text-primary">{trainingDance?.duration}</span>
          </div>
        </div>
        <div className="bg-brand-50 rounded-xl px-4 py-3 border border-brand/10 mb-5 text-center">
          <p className="text-sm font-semibold text-brand-dark">训练舞蹈模型</p>
          <p className="text-xs text-text-muted mt-0.5">包含：{trainProduct?.product_name ?? '舞蹈模型'}</p>
        </div>
        <div className="space-y-2 mb-5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-muted">当前橡树果</span>
            <span className="font-semibold text-text-primary">{trainBalance != null ? trainBalance : '--'}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-muted">本次消耗</span>
            <span className="font-semibold text-brand">{trainQuote != null ? `${trainQuote} 橡树果` : '--'}</span>
          </div>
        </div>
        <button onClick={handleConfirmTrain} disabled={trainLoading || trainQuote == null}
          className="w-full py-3.5 gradient-brand text-white rounded-xl text-sm font-semibold hover-lift disabled:opacity-50 disabled:cursor-not-allowed">
          {trainLoading ? '加载中...' : '确认训练'}
        </button>
      </Modal>
    </div>
  );
}
